export interface NewsletterIssue {
  id: string;
  slug: string;
  issueNumber: number;
  title: string;
  date: string;
  excerpt: string;
  category: string;
  tags: string[];
  readTime: string;
  body: NewsletterSection[];
}

export interface NewsletterSection {
  heading?: string;
  content: string;
}

export const mockNewsletterIssues: NewsletterIssue[] = [
  {
    id: 'nl-12',
    slug: 'issue-12-april-2026',
    issueNumber: 12,
    title: 'Spring Events, New Faces & Marketplace Highlights',
    date: 'April 1, 2026',
    excerpt:
      'April brings a packed events calendar, a warm welcome to new residents, and some standout marketplace finds from your neighbors.',
    category: 'Community Update',
    tags: ['Events', 'New Residents', 'Marketplace'],
    readTime: '4 min read',
    body: [
      {
        heading: 'Welcome to April',
        content:
          'Spring has arrived at Panorama Towers, and with it comes a full calendar of resident events, new faces in both towers, and a growing marketplace. This issue covers everything happening in the community this month.',
      },
      {
        heading: 'Upcoming Events',
        content:
          'April is packed. The Resident Mixer on April 4th at the Tower 2 Sky Lounge is shaping up to be one of the best-attended events of the year. The Dog Owners Meetup returns on April 12th in the courtyard. And the New Resident Welcome Social on April 24th is a great opportunity to meet the latest additions to our community.',
      },
      {
        heading: 'New Residents This Month',
        content:
          'We\'re pleased to welcome 6 new residents across both towers this month. If you see a new face in the elevator, say hello. The Welcome Social on April 24th is the perfect place to make introductions.',
      },
      {
        heading: 'Marketplace Highlights',
        content:
          'Some standout listings this month: a near-new Peloton Bike+ in Tower 2, a custom walnut dining table from a departing resident, and a set of professional photography lights. Check the marketplace for full details.',
      },
    ],
  },
  {
    id: 'nl-11',
    slug: 'issue-11-march-2026',
    issueNumber: 11,
    title: 'Resident Spotlight: Priya Mehta & the Wellness Initiative',
    date: 'March 1, 2026',
    excerpt:
      'This month we spotlight Priya Mehta, the resident behind the building\'s growing wellness program, plus updates on the yoga schedule and fitness class lineup.',
    category: 'Resident Spotlight',
    tags: ['Wellness', 'Yoga', 'Spotlight'],
    readTime: '5 min read',
    body: [
      {
        heading: 'Meet Priya Mehta',
        content:
          'Priya moved into Tower 1 eighteen months ago and has quietly become one of the most active contributors to community life at Panorama. A certified yoga instructor and wellness coach, she launched the pool deck yoga sessions last summer and has since grown attendance from 4 to nearly 20 regulars.',
      },
      {
        heading: 'The Wellness Initiative',
        content:
          'What started as an informal Saturday morning session has evolved into a structured wellness program. Priya now coordinates with James Okafor on the HIIT classes and is in conversation with building management about formalizing the fitness center schedule.',
      },
      {
        heading: 'March Yoga Schedule',
        content:
          'Yoga on the Pool Deck runs every Saturday at 7:30 AM through the end of March. All levels welcome. Bring your own mat. Blocks and straps are available on the deck. RSVP via the Events section so Priya can plan accordingly.',
      },
    ],
  },
  {
    id: 'nl-10',
    slug: 'issue-10-february-2026',
    issueNumber: 10,
    title: 'New Service Listings & Community Guidelines Update',
    date: 'February 1, 2026',
    excerpt:
      'February brings a wave of new service listings from residents, plus an important update to our community guidelines around marketplace conduct.',
    category: 'Services & Guidelines',
    tags: ['Services', 'Guidelines', 'Marketplace'],
    readTime: '3 min read',
    body: [
      {
        heading: 'New Services This Month',
        content:
          'Several residents have added new service listings this month. Notable additions include interior design consultations from Isabelle Fontaine, professional photography from Marcus Webb, and a new childcare availability listing from a Tower 2 resident.',
      },
      {
        heading: 'Community Guidelines Update',
        content:
          'We\'ve updated the community guidelines to clarify expectations around marketplace listings and service reviews. Key changes: listings must include accurate pricing, reviews must be from verified residents, and service providers may not solicit reviews directly.',
      },
    ],
  },
  {
    id: 'nl-9',
    slug: 'issue-9-january-2026',
    issueNumber: 9,
    title: 'Year in Review & What\'s Coming in 2026',
    date: 'January 5, 2026',
    excerpt:
      'A look back at 2025 — the events, the connections, and the growth of the Panorama community — and a preview of what\'s planned for the year ahead.',
    category: 'Community Update',
    tags: ['Year in Review', '2026', 'Community'],
    readTime: '6 min read',
    body: [
      {
        heading: '2025 in Numbers',
        content:
          'Over the past year, the Panorama community network has grown to include residents from both towers, with over 40 verified profiles, 12 newsletter issues, and more than 30 community events. The marketplace has seen over 80 listings, and the services directory has grown to include 24 resident-offered services.',
      },
      {
        heading: 'What\'s Coming in 2026',
        content:
          'This year we\'re focused on deepening the community experience. Planned additions include a more structured events calendar, a resident spotlight series in the newsletter, and expanded resources for new residents. We\'re also exploring a formal vouch system to strengthen trust across the network.',
      },
    ],
  },
  {
    id: 'nl-8',
    slug: 'issue-8-december-2025',
    issueNumber: 8,
    title: 'Holiday Social Recap & December Marketplace Picks',
    date: 'December 5, 2025',
    excerpt:
      'The holiday social brought both towers together for an evening to remember. Plus, our picks from the December marketplace.',
    category: 'Community Update',
    tags: ['Holiday', 'Social', 'Marketplace'],
    readTime: '3 min read',
    body: [
      {
        heading: 'Holiday Social Recap',
        content:
          'The annual holiday social at the Tower 2 Sky Lounge was a genuine success. Over 50 residents attended across both towers, making it the largest community event of the year. Special thanks to the events committee for organizing.',
      },
      {
        heading: 'December Marketplace Picks',
        content:
          'Some standout listings before the holidays: a set of vintage bar stools, a professional espresso machine, and a collection of art prints from a departing resident. All still available as of publication.',
      },
    ],
  },
  {
    id: 'nl-7',
    slug: 'issue-7-november-2025',
    issueNumber: 7,
    title: 'WhatsApp Community Guidance & Privacy Reminder',
    date: 'November 3, 2025',
    excerpt:
      'A reminder about how to use the building WhatsApp groups safely, plus our platform\'s approach to privacy and why it matters.',
    category: 'Privacy & Trust',
    tags: ['Privacy', 'WhatsApp', 'Trust'],
    readTime: '4 min read',
    body: [
      {
        heading: 'WhatsApp Groups & This Platform',
        content:
          'Several residents have asked about the relationship between the building WhatsApp groups and this platform. They serve different purposes. WhatsApp is great for quick, real-time communication. This platform is designed for structured, searchable, and privacy-respecting community interaction.',
      },
      {
        heading: 'Your Privacy Here',
        content:
          'On this platform, your phone number and email are hidden by default. Only information you choose to make visible is shown to other residents. We do not share your data with building management or third parties.',
      },
    ],
  },
];

export function getIssueBySlug(slug: string): NewsletterIssue | undefined {
  return mockNewsletterIssues.find((i) => i.slug === slug);
}
